import type {
  HomeDto,
  RotaPatternResponse,
  UpdateRotaPattern
} from '@lmc/ts-common'
import { postJSON } from 'common/services/api'
import { showToast } from 'common/utils'
import { useMutation, useQueryClient } from 'react-query'
import { useSelector } from 'react-redux'
import {
  getRotaPatternSubmitValues,
  type RotaPatternModalType
} from '../RotaPattern/utils'
import {
  ROTA_PATTERN_QUERY_KEY,
  ROTA_PATTERN_UPDATE_QUERY_KEY,
  ROTA_PATTERN_URL
} from './queryConstants'

type UseRotaPatternUpdateProps = {
  id?: string
  type?: RotaPatternModalType
  onUpdateSuccess?: () => void
}

type UseRotaPatternUpdateReturn = {
  updatePattern: (values: UpdateRotaPattern) => Promise<unknown>
  isLoadingUpdate?: boolean
}

export const useRotaPatternUpdate = ({
  id,
  type,
  onUpdateSuccess
}: UseRotaPatternUpdateProps): UseRotaPatternUpdateReturn => {
  const queryClient = useQueryClient()

  const handleError = (err: any) => {
    showToast({
      title: err?.message || err?.error,
      type: 'error'
    })
  }

  const home = useSelector(
    (state: { global: { home: HomeDto } }) => state.global.home
  )

  // UPDATE PATTERN
  const updateFunction = async (
    values: UpdateRotaPattern
  ): Promise<RotaPatternResponse> => {
    const patterFinalSubmitValues = getRotaPatternSubmitValues({
      type,
      home,
      values
    })

    return postJSON({
      url: `${ROTA_PATTERN_URL}/${id}`,
      method: 'PUT',
      json: patterFinalSubmitValues,
      isRota: true
    })
  }

  const { mutateAsync: updateMutation, isLoading: isLoadingUpdate } =
    useMutation<RotaPatternResponse, unknown, UpdateRotaPattern, unknown>(
      [ROTA_PATTERN_UPDATE_QUERY_KEY, id],
      updateFunction,
      {
        onSuccess: () => {
          queryClient.invalidateQueries(ROTA_PATTERN_QUERY_KEY)
          onUpdateSuccess?.()
        },
        onError: (err) => handleError(err)
      }
    )

  const handleUpdate = async (values: UpdateRotaPattern) => {
    try {
      const result = await updateMutation(values)
      return result
    } catch (err) {
      return err
    }
  }

  return {
    updatePattern: handleUpdate,
    isLoadingUpdate
  }
}
